import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  Mail, 
  Search,
  MessageSquare,
  Eye,
  Trash2,
  CheckCircle,
  Clock,
  Building2,
  Phone
} from 'lucide-react';
import { useAuth, API } from '@/App';

const ContactSubmissionsPage = () => {
  const { token } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/contact-submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(response.data);
    } catch (err) {
      toast.error('Failed to fetch contact submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (submission) => {
    setSelectedSubmission(submission);
    setViewDialogOpen(true);

    // Mark as read if new
    if (submission.status === 'new') {
      try {
        await axios.put(`${API}/contact-submissions/${submission.id}/status?status=read`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchSubmissions();
      } catch (err) {
        console.error('Failed to update status');
      }
    }
  };

  const handleStatusChange = async (submission, newStatus) => {
    try {
      await axios.put(`${API}/contact-submissions/${submission.id}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status updated');
      fetchSubmissions();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/contact-submissions/${selectedSubmission.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Submission deleted');
      setDeleteDialogOpen(false);
      setSelectedSubmission(null);
      fetchSubmissions();
    } catch (err) {
      toast.error('Failed to delete submission');
    }
  };

  const filteredSubmissions = submissions.filter(sub => {
    const matchesSearch = 
      sub.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sub.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (sub.company && sub.company.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (sub.message && sub.message.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || sub.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-700',
      read: 'bg-slate-100 text-slate-700',
      replied: 'bg-green-100 text-green-700',
      archived: 'bg-yellow-100 text-yellow-700'
    };
    return styles[status] || 'bg-slate-100 text-slate-700';
  };

  const newCount = submissions.filter(s => s.status === 'new').length;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Contact Submissions</h1>
          <p className="text-slate-600">View and manage contact form submissions</p>
        </div>
        {newCount > 0 && (
          <Badge className="bg-blue-500 text-white px-3 py-1">
            {newCount} New
          </Badge>
        )}
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Search submissions..." 
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="contact-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="read">Read</SelectItem>
                <SelectItem value="replied">Replied</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Submissions Table */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F3C4F]"></div>
            </div>
          ) : filteredSubmissions.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No contact submissions found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contact</TableHead>
                  <TableHead>Service Interest</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSubmissions.map((submission) => (
                  <TableRow 
                    key={submission.id} 
                    className={submission.status === 'new' ? 'bg-blue-50/50' : ''}
                    data-testid={`submission-row-${submission.id}`}
                  >
                    <TableCell>
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          {submission.name}
                          {submission.status === 'new' && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                          )}
                        </div>
                        <div className="text-sm text-slate-500">{submission.email}</div>
                        {submission.company && (
                          <div className="text-sm text-slate-400">{submission.company}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {submission.service_interest && (
                        <Badge variant="outline">{submission.service_interest}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="max-w-xs truncate text-slate-600">
                        {submission.message}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusBadge(submission.status)}>
                        {submission.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {formatDate(submission.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleView(submission)}
                          data-testid={`view-submission-${submission.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => { setSelectedSubmission(submission); setDeleteDialogOpen(true); }}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`delete-submission-${submission.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Contact Submission</DialogTitle>
          </DialogHeader>
          {selectedSubmission && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-slate-500">Name</div>
                    <div className="font-medium">{selectedSubmission.name}</div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-500">Email</div>
                    <a 
                      href={`mailto:${selectedSubmission.email}`} 
                      className="font-medium text-[#1F3C4F] hover:underline flex items-center gap-2"
                    >
                      <Mail className="w-4 h-4" />
                      {selectedSubmission.email}
                    </a>
                  </div>
                  {selectedSubmission.phone && (
                    <div>
                      <div className="text-sm text-slate-500">Phone</div>
                      <a 
                        href={`tel:${selectedSubmission.phone}`} 
                        className="font-medium flex items-center gap-2"
                      >
                        <Phone className="w-4 h-4" />
                        {selectedSubmission.phone}
                      </a>
                    </div>
                  )}
                </div>
                <div className="space-y-4">
                  {selectedSubmission.company && (
                    <div>
                      <div className="text-sm text-slate-500">Company</div>
                      <div className="font-medium flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        {selectedSubmission.company}
                      </div>
                    </div>
                  )}
                  {selectedSubmission.service_interest && (
                    <div>
                      <div className="text-sm text-slate-500">Service Interest</div>
                      <Badge variant="outline">{selectedSubmission.service_interest}</Badge>
                    </div>
                  )}
                  <div>
                    <div className="text-sm text-slate-500">Received</div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-slate-400" />
                      {formatDate(selectedSubmission.created_at)}
                    </div>
                  </div>
                </div>
              </div>

              {selectedSubmission.subject && (
                <div>
                  <div className="text-sm text-slate-500">Subject</div>
                  <div className="font-medium">{selectedSubmission.subject}</div>
                </div>
              )}

              <div>
                <div className="text-sm text-slate-500 mb-2">Message</div>
                <div className="bg-slate-50 rounded-lg p-4 whitespace-pre-wrap">
                  {selectedSubmission.message}
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Status:</span>
                  <Select 
                    value={selectedSubmission.status}
                    onValueChange={(value) => {
                      handleStatusChange(selectedSubmission, value);
                      setSelectedSubmission(prev => ({ ...prev, status: value }));
                    }}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="read">Read</SelectItem>
                      <SelectItem value="replied">Replied</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <a 
                  href={`mailto:${selectedSubmission.email}?subject=Re: ${selectedSubmission.subject || 'Your inquiry'}`}
                  className="inline-flex"
                >
                  <Button className="bg-[#1F3C4F] hover:bg-[#152a38]">
                    <Mail className="w-4 h-4 mr-2" />
                    Reply via Email
                  </Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Submission</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Are you sure you want to delete this contact submission from {selectedSubmission?.name}? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              data-testid="confirm-delete-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactSubmissionsPage;
